// React
import React, { Dispatch, useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

// Mantine
import {
  MRT_ColumnDef,
  MRT_Row,
  MRT_TableInstance,
  MRT_TableOptions,
} from 'mantine-react-table';
import {
  ActionIcon,
  Button,
  Group,
  Menu,
  Modal,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import {
  IconDotsVertical,
  IconPencil,
  IconUserPlus,
} from '@tabler/icons-react';

// Refine
import { useNotification } from '@refinedev/core';
import { useDocumentTitle } from '@refinedev/react-router-v6';

// Custom Components
import { ProviderColumns } from 'columns/Provider';
import { EditModal } from 'components/common';
import { BillingDataGridWrapper } from 'components/common/BillingDataGridWrapper';
import { TableAlertBanner } from 'components/common/TableAlertBanner';
import { TableProgressBar } from 'components/common/TableProgressBar';
import { uploadFileToS3 } from 'utils/fileUpload';
import { ProviderTopToolbar } from './components/ProviderTopToolbar';

// Interface
import { Payor, Provider, Role, Site, User } from 'interfaces';

// Resource
import {
  addProviderToSite,
  BulkProviderIngestJob,
  BulkProviderIngestResult,
  bulkIngestProviders,
  getBulkProviderIngestJob,
  useCreateProvider,
  useCreateUser,
  useUpdateProvider,
} from 'api/resource';
import {
  CreateMutationDataParams,
  UpdateMutationDataParams,
  updateMutationData,
} from 'api/utils/MutationDataBuilder';

// Auth
import { useIsAdmin, useCanModify } from 'hooks';

// Sentry
import * as Sentry from '@sentry/react';
import { ProviderCreateModal } from './components';
import { v4 } from 'uuid';
import { hasInvalidBillingNumbers } from '@billing/validation';

interface ProvidersManagementProps {
  providers: Provider[];
  payors: Payor[];
  roles: Role[];
  currentSite: Site;
  isLoading: boolean;
  isError: boolean;
  isFetching: boolean;
  refetchProviders: () => void;
  errorMessage: string;
  setIsLoading: Dispatch<React.SetStateAction<boolean>>;
}

export const ProvidersManagement: React.FC<ProvidersManagementProps> = ({
  providers,
  payors,
  roles,
  isLoading,
  currentSite,
  isError,
  isFetching,
  errorMessage,
  setIsLoading,
  refetchProviders,
}) => {
  const title = 'Providers';
  useDocumentTitle('Providers | QBilling');
  const isAdmin = useIsAdmin();
  const { canModify } = useCanModify('providers');

  const providersBucket = `provider-files-${import.meta.env.VITE_ACCOUNT_ID}`;

  const [addProviderModal, setAddProviderModal] = useState<boolean>(false);
  const [uploadModal, setUploadModal] = useState<boolean>(false);
  const [portalUserProvider, setPortalUserProvider] = useState<Provider | null>(
    null
  );
  const [portalUserEmail, setPortalUserEmail] = useState<string>('');
  const [portalUserEmailError, setPortalUserEmailError] = useState<
    string | null
  >(null);
  const openRef = useRef<() => void>(null);
  const [providerFile, setProviderFile] = useState<File[]>([]);
  const [providerUploadResult, setProviderUploadResult] =
    useState<BulkProviderIngestResult | null>(null);
  const [providerUploadJob, setProviderUploadJob] =
    useState<BulkProviderIngestJob | null>(null);
  const [isUploadingProviders, setIsUploadingProviders] =
    useState<boolean>(false);
  const { open } = useNotification();

  const createProvider = useCreateProvider();
  const updateProvider = useUpdateProvider();
  const createUser = useCreateUser();
  const providerUploadJobId = providerUploadJob?.jobId;

  const providerUploadJobQuery = useQuery({
    queryKey: [
      'provider-bulk-ingest-job',
      providersBucket,
      providerUploadJobId,
    ],
    queryFn: () => {
      if (!providerUploadJobId) {
        throw new Error('Provider upload job is missing');
      }
      return getBulkProviderIngestJob({
        bucket: providersBucket,
        jobId: providerUploadJobId,
      });
    },
    enabled: Boolean(
      providerUploadJobId && isUploadingProviders && !providerUploadResult
    ),
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      if (
        status === 'COMPLETED' ||
        status === 'COMPLETED_WITH_ERRORS' ||
        status === 'FAILED'
      ) {
        return false;
      }
      return 2000;
    },
    refetchIntervalInBackground: true,
    retry: false,
  });

  useEffect(() => {
    const job = providerUploadJobQuery.data;
    if (!job) return;

    setProviderUploadJob(job);

    if (job.status === 'FAILED') {
      open?.({
        key: 'error-uploading-file',
        message: 'There was an error uploading the file',
        description: job.error,
        type: 'error',
      });
      setIsUploadingProviders(false);
      return;
    }

    if (job.status !== 'COMPLETED' && job.status !== 'COMPLETED_WITH_ERRORS') {
      return;
    }

    if (!job.result) {
      open?.({
        key: 'error-uploading-file',
        message: 'Provider upload completed without a result',
        type: 'error',
      });
      setIsUploadingProviders(false);
      return;
    }

    setProviderUploadResult(job.result);
    refetchProviders();
    open?.({
      key: 'file-uploaded',
      message: 'Provider upload complete',
      description: `${job.result.createdCount} created, ${job.result.skippedCount} skipped, ${job.result.failedCount} failed`,
      type: job.result.failedCount ? 'error' : 'success',
    });
    setIsUploadingProviders(false);
  }, [open, providerUploadJobQuery.data, refetchProviders]);

  useEffect(() => {
    if (!providerUploadJobQuery.error) return;

    open?.({
      key: 'error-uploading-file',
      message: 'There was an error uploading the file',
      type: 'error',
    });
    Sentry.captureException(providerUploadJobQuery.error);
    setIsUploadingProviders(false);
  }, [open, providerUploadJobQuery.error]);

  const handleCreateProvider = async ({
    values,
    table,
  }: CreateMutationDataParams<
    Provider & {
      createUser?: boolean;
    }
  >) => {
    const newProvider: Provider = {
      ...values,
      id: v4(),
      specialtyCode: values?.specialty?.code || '99999',
      siteID: currentSite.id,
    };
    if (!isAdmin) {
      return;
    }
    if (hasInvalidBillingNumbers(newProvider.licenses, payors)) {
      return;
    }
    if (values.createUser && !newProvider.email) {
      open?.({
        key: 'provider-user-email-required',
        message: 'Provider Email Required',
        description: 'Enter a provider email before creating portal access.',
        type: 'error',
      });
      return;
    }
    if (newProvider.groups.length > 0) {
      newProvider.groups = newProvider.groups.map((group) => ({
        id: v4(),
        name: group.name,
        siteID: currentSite.id,
        providerID: newProvider.id,
      }));
    }
    setIsLoading(true);
    let provider: Provider | null = null;
    try {
      provider = await createProvider.mutateAsync(newProvider);
      await addProviderToSite({
        providerID: provider.id,
        siteID: currentSite.id,
      });
      // Provider portal user creation validates that the provider belongs to
      // the selected site, so attach the provider before creating access.
      if (values.createUser && !provider.userID && !provider.user) {
        await handleCreateUser(provider, provider.email ?? undefined);
      }
      refetchProviders();
    } catch (error) {
      Sentry.captureException(error);
    } finally {
      setIsLoading(false);
      table.setCreatingRow(null);
    }
  };
  const handleEditProvider = async ({
    table,
    values,
    row,
  }: UpdateMutationDataParams<Provider>) => {
    if (hasInvalidBillingNumbers(values.licenses, payors)) {
      return;
    }
    try {
      setIsLoading(true);
      await updateMutationData<Provider>({
        table,
        values: { ...values, siteID: currentSite.id },
        row,
        mutation: updateProvider,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmitFile = async () => {
    let shouldKeepUploading = false;
    try {
      if (providerFile.length !== 1) {
        open?.({
          key: 'error-uploading-file',
          message: 'Select one provider file',
          type: 'error',
        });
        return;
      }

      const selectedFile = providerFile[0];
      if (!selectedFile.name.toLowerCase().endsWith('.txt')) {
        open?.({
          key: 'error-uploading-file',
          message: 'Provider file must be a .txt file',
          type: 'error',
        });
        return;
      }

      setIsUploadingProviders(true);
      setProviderUploadResult(null);
      setProviderUploadJob(null);
      await uploadFileToS3(providerFile, providersBucket);
      const ingestResponse = await bulkIngestProviders(
        providersBucket,
        providerFile
      );
      if ('jobId' in ingestResponse) {
        shouldKeepUploading = true;
        setProviderUploadJob(ingestResponse);
        return;
      }

      const result = ingestResponse;
      setProviderUploadResult(result);
      refetchProviders();
      open?.({
        key: 'file-uploaded',
        message: 'Provider upload complete',
        description: `${result.createdCount} created, ${result.skippedCount} skipped, ${result.failedCount} failed`,
        type: result.failedCount ? 'error' : 'success',
      });
    } catch (error) {
      open?.({
        key: 'error-uploading-file',
        message: 'There was an error uploading the file',
        type: 'error',
      });
      Sentry.captureException(error);
    } finally {
      if (!shouldKeepUploading) {
        setIsUploadingProviders(false);
      }
    }
  };
  const handleCreateUser = async (provider: Provider, email?: string) => {
    const providerEmail = email ?? provider.email;
    if (!providerEmail) {
      open?.({
        key: 'provider-user-email-required',
        message: 'Provider Email Required',
        description: 'Enter a provider email before creating portal access.',
        type: 'error',
      });
      return null;
    }

    if (provider.userID || provider.user) {
      open?.({
        key: 'provider-user-already-linked',
        message: 'Provider Already Has Portal Access',
        description: 'This provider is already associated with a user.',
        type: 'error',
      });
      return null;
    }

    const providerRole = roles?.find((role) => role.name === 'Provider');
    const userData: Partial<User> & {
      userRole: 'PROVIDER';
      allowedSiteIDs: string[];
      selectedSiteID: string;
    } = {
      firstName: provider?.firstName,
      lastName: provider?.lastName,
      email: providerEmail,
      phone: '+12045555555',
      status: 'ACTIVE',
      roleID: providerRole?.id || roles[0]?.id,
      organizationID: currentSite?.organizationID,
      providerID: provider.id,
      cognitoID: '',
      role: providerRole || roles[0],
      userRole: 'PROVIDER',
      allowedSiteIDs: [currentSite.id],
      selectedSiteID: currentSite.id,
    };

    try {
      const user = await createUser.mutateAsync(userData);
      return user;
    } catch (error) {
      Sentry.captureException(error);
      return null;
    }
  };

  const handleOpenPortalUserModal = (provider: Provider) => {
    setPortalUserProvider(provider);
    setPortalUserEmail(provider.email ?? '');
    setPortalUserEmailError(null);
  };

  const handleClosePortalUserModal = () => {
    setPortalUserProvider(null);
    setPortalUserEmail('');
    setPortalUserEmailError(null);
  };

  const handleSubmitPortalUser = async () => {
    if (!portalUserProvider) return;
    if (!portalUserEmail.trim()) {
      setPortalUserEmailError('Provider email is required.');
      return;
    }

    setIsLoading(true);
    try {
      const user = await handleCreateUser(
        portalUserProvider,
        portalUserEmail.trim()
      );
      if (user) {
        refetchProviders();
        handleClosePortalUserModal();
      }
    } finally {
      setIsLoading(false);
    }
  };

  const data = providers ?? ([] as Provider[]);
  const columns: MRT_ColumnDef<Provider>[] = ProviderColumns({
    payors,
  });
  const tableOptions: MRT_TableOptions<Provider> = {
    data,
    columns,
    state: {
      showLoadingOverlay: isFetching || isLoading,
      showAlertBanner: isError,
      showProgressBars: isFetching || isLoading,
      columnVisibility: {
        createUser: false,
        contactNote: false,
        email: false,
        lastName: false,
        specialtyStartDate: false,
      },
    },
    initialState: {
      columnVisibility: {
        email: false,
        lastName: false,
        specialtyStartDate: false,
      },
    },
    createDisplayMode: 'modal',
    editDisplayMode: 'modal',
    enableFullScreenToggle: false,
    enableEditing: isAdmin,
    displayColumnDefOptions: {
      'mrt-row-actions': {
        header: 'Actions',
        Cell: ({
          row,
          table,
        }: {
          row: MRT_Row<Provider>;
          table: MRT_TableInstance<Provider>;
        }) => {
          const providerHasPortalUser = !!(
            row.original.userID || row.original.user
          );
          return (
            <Menu shadow="md" width={190} position="right-start" withinPortal>
              <Menu.Target>
                <ActionIcon
                  variant="subtle"
                  aria-label="Provider actions"
                  data-testid={`provider-actions-menu-${row.original.id}`}
                  onClick={(event) => {
                    event.stopPropagation();
                  }}
                >
                  <IconDotsVertical size={18} stroke={1.5} />
                </ActionIcon>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Label>Actions</Menu.Label>
                <Menu.Item
                  leftSection={<IconPencil size={14} />}
                  onClick={() => table.setEditingRow(row)}
                  disabled={!canModify}
                  data-testid="edit-provider-button"
                >
                  Edit Provider
                </Menu.Item>
                {!providerHasPortalUser && (
                  <Menu.Item
                    leftSection={<IconUserPlus size={14} />}
                    onClick={() => handleOpenPortalUserModal(row.original)}
                    disabled={!canModify}
                    data-testid={`create-portal-user-${row.original.id}`}
                  >
                    Invite To QBilling
                  </Menu.Item>
                )}
              </Menu.Dropdown>
            </Menu>
          );
        },
        size: 70,
      },
    },
    mantineLoadingOverlayProps: { visible: isLoading },
    mantineProgressProps({ isTopToolbar }) {
      return TableProgressBar({ isTopToolbar });
    },
    mantineToolbarAlertBannerProps() {
      return TableAlertBanner({ isError, message: errorMessage });
    },
    mantineEditRowModalProps: {
      withCloseButton: true,
      size: 'lg',
      centered: true,
      withOverlay: true,
      overlayProps: {
        backgroundOpacity: 0.5,
        blur: 5,
      },
    },
    onCreatingRowSave: handleCreateProvider,
    onEditingRowSave: handleEditProvider,
    renderTopToolbarCustomActions: ({ table }) => (
      <ProviderTopToolbar
        table={table}
        providers={providers}
        payors={payors}
        currentSite={currentSite}
        setAddProviderModal={setAddProviderModal}
        setUploadModal={(value) => {
          if (value) {
            setProviderFile([]);
            setProviderUploadResult(null);
            setProviderUploadJob(null);
          }
          setUploadModal(value);
        }}
        openRef={openRef}
        providerFile={providerFile}
        setProviderFile={(files) => {
          setProviderFile(files);
          setProviderUploadResult(null);
          setProviderUploadJob(null);
        }}
        handleSubmitFile={handleSubmitFile}
        uploadResult={providerUploadResult}
        uploadJob={providerUploadJob}
        isUploadingProviders={isUploadingProviders}
        addProviderModal={addProviderModal}
        uploadModal={uploadModal}
        handleCreateUser={handleCreateUser}
      />
    ),
    renderCreateRowModalContent: ({ table, row, internalEditComponents }) =>
      ProviderCreateModal({
        table,
        row,
        internalEditComponents,
      }),
    renderEditRowModalContent: ({ table, row, internalEditComponents }) =>
      EditModal<Provider>({
        type: 'provider',
        row,
        table,
        internalEditComponents,
        alertModal: false,
        isLoading,
      }),
  };

  return (
    <>
      <Modal
        opened={!!portalUserProvider}
        onClose={handleClosePortalUserModal}
        title="Create Portal User"
        centered
      >
        <Stack>
          <Text size="sm">
            Create portal access for {portalUserProvider?.firstName}{' '}
            {portalUserProvider?.lastName}.
          </Text>
          <TextInput
            label="Email"
            value={portalUserEmail}
            onChange={(event) => {
              setPortalUserEmail(event.currentTarget.value);
              setPortalUserEmailError(null);
            }}
            error={portalUserEmailError}
            data-testid="portal-user-email-input"
            required
          />
          <Group justify="flex-end">
            <Button variant="subtle" onClick={handleClosePortalUserModal}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmitPortalUser}
              loading={isLoading}
              data-testid="submit-create-portal-user"
            >
              Create Portal User
            </Button>
          </Group>
        </Stack>
      </Modal>
      <BillingDataGridWrapper
        title={title}
        tableOptions={tableOptions}
        dataTestID="providerManagement"
        showAlertBanner={false}
      />
    </>
  );
};
